import { useCallback, useMemo } from "react"

import { useAuth0 } from "@auth0/auth0-react"

import ErrorWithData from "@/utils/ErrorWithData"

import { GeneratedMutation, GeneratedQuery } from "@/types/GQLTypes"

// import { generateClient } from "aws-amplify/api"

export function useAppSync() {
  const { getAccessTokenSilently } = useAuth0()

  const query = useCallback(
    async <TInput, TOutput>(options: { query: string; variables: TInput }, fetchOptions: RequestInit = {}) => {
      const authToken = await getAccessTokenSilently()
      // TODO: use generateClient
      // const client = generateClient({ authMode: "oidc", authToken })
      // return client.graphql(typeof options === "string" ? { query: options } : (options as any))

      return fetch(import.meta.env.VITE_PUBLIC_APPSYNC_ENDPOINT as string, {
        method: "POST",
        headers: {
          authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(options),
        ...fetchOptions,
      })
        .then((res) => res.json())
        .then(({ data, errors }) => {
          if (errors && (!data || Object.values(data).filter(Boolean).length <= 0)) {
            throw new ErrorWithData("Error with query", errors)
          }
          return data as TOutput
        })
    },
    [getAccessTokenSilently]
  )

  const fetchAll = useCallback(
    async <Q extends GeneratedQuery<any, any>>(
      options: { query: Q; variables: Q["__generatedQueryInput"] },
      queryKey: string,
      fetchOptions?: RequestInit
    ): Promise<Q["__generatedQueryOutput"][]> => {
      const allData: Q["__generatedQueryOutput"][] = []
      const data = await query<Q["__generatedQueryInput"], Q["__generatedQueryOutput"]>(options, fetchOptions)
      allData.push(data)
      if (data?.[queryKey]?.nextToken) {
        let { nextToken } = data[queryKey]
        while (nextToken) {
          const nextOptions = {
            ...options,
            variables: {
              ...options.variables,
              nextToken,
            },
          }
          // eslint-disable-next-line no-await-in-loop
          const nextData = await query<Q["__generatedQueryInput"], Q["__generatedQueryOutput"]>(
            nextOptions,
            fetchOptions
          )
          allData.push(nextData)
          nextToken = nextData?.[options.query]?.nextToken
        }
      }

      return allData
    },
    [query]
  )

  const fetcher = useCallback(
    <T extends GeneratedQuery<any, any>>([_query, variables]: [T, T["__generatedQueryInput"]]) => {
      return query<T["__generatedQueryInput"], T["__generatedQueryOutput"]>({ query: _query, variables })
    },
    [query]
  )

  const output = useMemo(
    () => ({
      query: <T extends GeneratedQuery<any, any>>(
        options: { query: T; variables: T["__generatedQueryInput"] },
        fetchOptions?: RequestInit
      ) => query<T["__generatedQueryInput"], T["__generatedQueryOutput"]>(options, fetchOptions),
      mutation: <T extends GeneratedMutation<any, any>>(
        options: { query: T; variables: T["__generatedMutationInput"] },
        fetchOptions?: RequestInit
      ) => query<T["__generatedMutationInput"], T["__generatedMutationOutput"]>(options, fetchOptions),
      fetcher,
      fetchAll,
    }),
    [fetchAll, fetcher, query]
  )

  return output
}

export default useAppSync
